import { useRef, useEffect, useState, useCallback } from 'react'

/**
 * Component that automatically scales down font size when text would overflow
 * @param {Object} props
 * @param {string} props.as - HTML tag to render (default: 'span')
 * @param {number} props.maxFontSize - Maximum font size in px
 * @param {number} props.minFontSize - Minimum font size in px (default: 12)
 * @param {React.ReactNode} props.children - Text content
 * @param {Object} props.style - Additional styles
 * @param {string} props.className - Additional class name
 */
const FitText = ({ 
    as: Tag = 'span', 
    maxFontSize, 
    minFontSize = 12, 
    children, 
    style = {},
    className,
    ...props 
}) => {
    const containerRef = useRef(null)
    const measureRef = useRef(null)
    const [fontSize, setFontSize] = useState(maxFontSize)

    const calculateFontSize = useCallback(() => {
        if (!containerRef.current || !measureRef.current) return

        const container = containerRef.current
        const measureSpan = measureRef.current

        // Measure at max font size
        measureSpan.style.fontSize = `${maxFontSize}px`
        
        const containerWidth = container.offsetWidth
        const textWidth = measureSpan.scrollWidth

        if (textWidth > containerWidth && containerWidth > 0) {
            // Calculate the scale ratio
            const ratio = containerWidth / textWidth
            const newFontSize = Math.max(
                Math.floor(maxFontSize * ratio * 0.95), // 0.95 for some padding
                minFontSize
            )
            setFontSize(newFontSize)
        } else {
            setFontSize(maxFontSize)
        }
    }, [maxFontSize, minFontSize])

    useEffect(() => {
        calculateFontSize()

        // Recalculate on window resize
        const handleResize = () => {
            requestAnimationFrame(calculateFontSize)
        }

        window.addEventListener('resize', handleResize)
        
        // Use ResizeObserver for container size changes
        const resizeObserver = new ResizeObserver(() => {
            requestAnimationFrame(calculateFontSize)
        })
        
        if (containerRef.current) {
            resizeObserver.observe(containerRef.current)
        }

        return () => {
            window.removeEventListener('resize', handleResize)
            resizeObserver.disconnect()
        }
    }, [calculateFontSize, children, maxFontSize]) // Recalculate when children or maxFontSize change

    return (
        <Tag 
            ref={containerRef} 
            className={className}
            style={{ 
                ...style, 
                display: 'block',
                width: '100%',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                fontSize: `${fontSize}px`,
                fontFamily: 'Pretendard',
                fontWeight: '600',
            }}
            {...props}
        >
            {children}
            {/* Hidden span for measuring text at max font size */}
            <span
                ref={measureRef}
                style={{
                    position: 'absolute',
                    visibility: 'hidden',
                    whiteSpace: 'nowrap',
                    fontFamily: 'inherit',
                    fontWeight: 'inherit',
                }}
                aria-hidden="true"
            >
                {children}
            </span>
        </Tag>
    )
}

export default FitText

